import { Toast, toast } from "react-hot-toast";
import { cn } from "./cn";
import Button from "../components/atoms/button";
import GetHeroIcon from "./getHeroIcon";
import Tooltip from "../components/atoms/tooltip";
import { getAuthoringLibraryLabel } from "./authoringLibraryResources/localizeAuthoringLibraryLabels";

interface ShowToastConfig {
  severity: "error" | "warning" | "success" | "info" | "event-change";
  label: string;
  description?: string;
  messageList?: string[];
  actionButtonClick?: () => void;
  autoClose?: boolean;
  duration?: number;
  position?:
    | "top-right"
    | "top-center"
    | "top-left"
    | "bottom-right"
    | "bottom-center"
    | "bottom-left";
  language?: string;
}

interface ShowToastReturnType {
  toastId: string;
  dismiss: () => void;
}

const injectKeyframes = () => {
  const id = "toast-animations";
  if (document.getElementById(id)) return;

  const style = document.createElement("style");
  style.id = id;
  style.innerHTML = `
    @keyframes slideInRight {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0%);
        opacity: 1;
      }
    }
    @keyframes slideOutRight {
      from {
        transform: translateX(0%);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);
};

export const ShowToast = ({
  label,
  severity,
  actionButtonClick,
  description,
  messageList,
  duration,
  position,
  autoClose = true,
  language = "en"
}: ShowToastConfig): ShowToastReturnType => {
  injectKeyframes();

  const custonToastComponent = (t: Toast): JSX.Element => {
    const animatedStyle =
      severity === "event-change"
        ? {
            animation: t.visible
              ? "slideInRight 0.3s ease-out forwards"
              : "slideOutRight 0.2s ease-in forwards",
            zIndex: 99999
          }
        : { zIndex: 99999 };

    return (
      <div
        style={animatedStyle}
        className={cn(
          "w-[26rem] max-w-[35rem] px-4 py-3 border shadow-sm rounded-lg",
          severity === "success" && "bg-[#f0fdf4]",
          severity === "error" && "bg-[#fef2f2]",
          severity === "warning" && "bg-[#fefce8]",
          severity === "info" && "bg-[#eff6ff]",
          severity === "event-change" && "bg-[#ffffff]"
        )}
      >
        <div
          className={cn(
            "flex",
            severity === "success" && "text-green-800",
            severity === "error" && "text-red-800",
            severity === "warning" && "text-yellow-800",
            severity === "info" && "text-blue-800",
            severity === "event-change" && "text-black-800"
          )}
        >
          <div className="mr-2">
            {severity === "success" && (
              <GetHeroIcon
                variant="solid"
                name="CheckCircleIcon"
                className="w-5 h-5 text-green-400"
              />
            )}
            {severity === "error" && (
              <GetHeroIcon
                variant="solid"
                name="XCircleIcon"
                className="w-5 h-5 text-red-400"
              />
            )}
            {severity === "info" && (
              <GetHeroIcon
                variant="solid"
                name="InformationCircleIcon"
                className="w-5 h-5 text-blue-400"
              />
            )}
            {severity === "warning" && (
              <GetHeroIcon
                variant="solid"
                name="ExclamationTriangleIcon"
                className="w-5 h-5 text-yellow-400"
              />
            )}
          </div>
          <div>
            <label
              className={cn(
                "text-sm font-medium",
                severity === "event-change" ? "text-black" : "text-inherit"
              )}
            >
              {label}
            </label>
            {description && (
              <p
                className={cn(
                  "text-sm",
                  severity === "event-change" ? "text-black" : "text-inherit"
                )}
              >
                {description}
              </p>
            )}
            {messageList && (
              <ul className="w-full mb-3 space-y-2 list-disc list-inside">
                {messageList.map((msg, id) => (
                  <li
                    key={id}
                    className={cn(
                      "text-sm leading-tight break-words",
                      severity === "success" && "text-green-800",
                      severity === "error" && "text-red-800",
                      severity === "info" && "text-blue-800",
                      severity === "warning" && "text-yellow-800",
                      severity === "event-change" && "text-black-800"
                    )}
                  >
                    {msg}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="inline-flex justify-end w-full pr-3 my-1 ml-4">
          <Button
            label={getAuthoringLibraryLabel("dismiss", language)}
            type="button"
            variant="default"
            className={cn(
              "py-1 px-3 rounded-md",
              severity === "error" &&
                "text-red-800 bg-red-50 hover:bg-red-300 focus:ring-red-500 focus:ring-offset-red-50",
              severity === "info" &&
                "text-blue-700 bg-blue-50 hover:bg-blue-200 focus:ring-blue-500 focus:ring-offset-blue-50",
              severity === "warning" &&
                "text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:ring-yellow-500 focus:ring-offset-yellow-50",
              severity === "success" &&
                "bg-green-50 text-green-800 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50",
              severity === "event-change" &&
                "bg-green-50 text-green-800 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50"
            )}
            onClick={(e) => {
              e.preventDefault();
              toast.dismiss(t.id);
              if (actionButtonClick) {
                actionButtonClick();
              }
            }}
            testId="dismissErrorListBtn"
          />
        </div>
      </div>
    );
  };

  const toastId = toast.custom((t: any) => custonToastComponent(t), {
    duration: !autoClose ? Infinity : duration || 5000,
    style: { zIndex: "99999" },
    ...(position && { position })
  });

  const dismissToast = () => {
    if (toastId) {
      toast.dismiss(toastId);
    }
  };
  return { toastId: toastId!, dismiss: dismissToast };
};
